import { useEffect, useCallback, useState } from 'react';
import { Editor, EditorEvents } from '@tiptap/core';
import {
  convertParagraphsToNewline,
  EditorType,
  toStringOrNull,
} from 'screens/editorial/asset/utility/getEditorConfigs';
import { useDebouncedCallback } from '@mantine/hooks';
import { isNumber } from 'remeda';
import { Transaction } from '@tiptap/pm/state';

export type CustomEditorOnChangeProps = {
  editor?: Editor;
  transaction?: EditorEvents['update']['transaction'];
  result?: any; // processed 'new content' eg text, html, string | number | string[]
};

type DbHandlerArgs = {
  editor: Editor;
  transaction?: Transaction;
};

export type CustomEditorOnChangeFn = (args: CustomEditorOnChangeProps) => void;

export type UseDebouncedOnChangeProps = {
  delay: number;
  onChange: CustomEditorOnChangeFn;
  type?: EditorType;
  editor?: Editor;
};

type ProcessEditorChangeArgs = {
  editor?: Editor;
  transaction?: EditorEvents['update']['transaction'];
  type?: EditorType;
  onChange: CustomEditorOnChangeFn;
};

/**
 * Process the editors contents based on the
 * type and call the onChange callback with the
 * returned formatted content, which generally
 * will be set to the form values state.
 */

function processEditorChange({
  editor,
  transaction,
  type,
  onChange,
}: ProcessEditorChangeArgs) {
  // Content processing logic
  const emptyParagraphRegex = /<p>\s*<\/p>/i;

  // Process tiptap contents to be returned to the parent
  // component and generally will be setValued to the form
  let processed;

  if (editor) {
    // Guard against destroyed editors during flushOnUnmount or teardown
    if (editor.isDestroyed) {
      return;
    }

    switch (type) {
      case 'text':
      case 'code': {
        processed = editor.getText();
        break;
      }
      case 'number':
      case 'coordinate': {
        // Don't convert Number(null) to 0
        // dont set NaN or Infinity back to the editor
        const parsed = parseFloat(editor.getText());
        const isValid = Number.isFinite(parsed) && isNumber(parsed);
        if (isValid) {
          processed = parsed;
        } else {
          processed = null;
        }
        break;
      }
      case 'textarea': {
        // Replace <p> tags with newlines
        processed = convertParagraphsToNewline(editor.getHTML());
        break;
      }
      case 'array': {
        processed = editor.getHTML();

        // Note quirk of tiptap for array type if editor was init as 'empty'
        // (eg the getEditorConfig contentForEditor was null or empty string)
        // or if it becomes empty tiptap will just have "<p></p>" so extracting
        // from tags may result in [""] and we need just an empty [] instead, so
        // generally this case shouldnt allow empty str in arr as values

        // Convert TipTap content to array for form consumption
        const parser = new DOMParser();
        const doc = parser.parseFromString(processed, 'text/html');
        const paragraphElements = doc.querySelectorAll('p');
        // Get the text content of each paragraph element,
        const paragraphTxt = Array.from(paragraphElements).map(
          (item) => item.textContent,
        );
        // filtering out empty tags or nulls  the
        // final result is a valid string array
        const strArray = paragraphTxt
          .map((text) => {
            // numbers, null, undefined, empty strings
            // objects and booleans not valid
            // skip nulls and filter out after map
            const cleaned = toStringOrNull(text);
            if (cleaned === null) {
              return null;
            }
            return cleaned;
          })
          .filter((text) => text !== null);

        processed = strArray;
        break;
      }
      case 'custom': {
        // if field type custom and custom onChange, just fwding the entire editor
        // and txn to the parent and using the using getHTML as the default fallback
        // cb can then can access txn/editor/field/etc and do whatever it needs to
        processed = editor.getHTML();
        break;
      }
      default: {
        processed = editor.getHTML();
        // Remove empty paragraphs
        if (emptyParagraphRegex.test(processed)) {
          processed = processed.replace(emptyParagraphRegex, '');
        }
        break;
      }
    }

    // send result to passed onChange cb
    onChange({
      editor: editor,
      transaction: transaction,
      result: processed,
    });
  }
}

export const useDebouncedOnChange = ({
  delay,
  onChange,
  type,
  editor,
}: UseDebouncedOnChangeProps) => {
  // Track if we have ran the initial mount sync
  const [hasInit, setHasInit] = useState(false);

  // Process the editor update
  const debouncedUpdateHandler = useDebouncedCallback(
    ({ editor, transaction }: DbHandlerArgs) => {
      processEditorChange({
        editor,
        transaction,
        type,
        onChange,
      });
    },
    {
      delay: delay,
      flushOnUnmount: true,
    },
  );

  const onEditorUpdate = useCallback(
    ({ editor, transaction }: EditorEvents['update']) => {
      // Quickly filters out most transaction/updates that
      // tiptap thinks did not actually change the content.
      // Saves some processing work for the
      // debouncedUpdateHandler. See more notes
      // on this interaction in useCreateEditor
      // about initial setcontent emitting update...
      if (!transaction?.docChanged) {
        return;
      }

      debouncedUpdateHandler({ editor, transaction });
    },
    [debouncedUpdateHandler],
  );

  const onEditorCreate = useCallback(
    ({ editor }: EditorEvents['create']) => {
      debouncedUpdateHandler({ editor });
    },
    [debouncedUpdateHandler],
  );
  // 1. Register editor transaction listeners
  // to handle editor content changes...
  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.on('update', onEditorUpdate);
    editor.on('create', onEditorCreate);
    return () => {
      editor.off('update', onEditorUpdate);
      editor.off('create', onEditorCreate);
    };
  }, [editor, onEditorUpdate, onEditorCreate]);

  // 2. Once, when editor is ready and the
  // component is mounted, run the update handler.
  // See notes below for more details.
  useEffect(() => {
    if (hasInit) {
      return;
    }
    if (!editor) {
      return;
    }
    if (!editor.isInitialized) {
      return;
    }
    if (editor.isDestroyed) {
      return;
    }

    // Run the update handler once to sync initial content
    debouncedUpdateHandler({ editor });
    setHasInit(true);
  }, [editor, editor?.isInitialized, hasInit, debouncedUpdateHandler]);
};

/**
 * Note 2: in collab mode, if loading from cloud
 * the on create/update/txn editor events are not
 * emitted consistently on load, or are emitted before mount
 * so the one-time useEffect is to ensure when the
 * component is mounted and editor ready we invoke the
 * update handler, once, to sync the form state / run any
 * onChange logic specific components have specified.
 *
 * Tiptap was only emitting events on load for certain fields
 * like body copy, title, etc but not other fields like tagline.
 *
 * This was noticed in QA if you create a doc in window A, open
 * window B, make changes in window A to eg tagline, and
 * then refresh window B. The content was visible on both
 * windows, but the onChange in window B was not triggered
 * to set it to the form state. So if you save in window B
 * without making any changes in either window, the tagline
 * field payload was blanked out as the form state wasnt updated.
 * Only if either A/B made a change in the editor would the
 * onChange be triggered and the form state updated.
 *
 * ---
 *
 * Transactions will almost always fire in tiptap for everything
 * even if the content did not change, however it can
 * possibly be used as a last resort for detecting events
 * since it is emitted so often... generally in collab the one
 * that seems to be emitted the most is y-cursor. Registering
 * or emitting our own events in useCreateEditor onCreate sometimes
 * is not caught down here likely due to component mount order/times.
 *
 *  const onEditorTransaction = useCallback(
 *      ({ editor, transaction }: EditorEvents['transaction']) => {
 *        debouncedUpdateHandler({ editor, transaction });
 *      },
 *      [debouncedUpdateHandler],
 *   );
 *
 * editor.on('transaction', onEditorTransaction);
 * editor.off('transaction', onEditorTransaction);
 *
 *
 */
